import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ShoppingCart,
  ArrowLeft,
  Edit,
  Globe,
  Webhook,
  ExternalLink,
  CheckCircle,
  XCircle as XCircleIcon,
  AlertCircle,
  Loader2,
  Settings,
  Search,
  ChevronDown,
  Star,
  CreditCard,
  RefreshCw,
  Eye,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/ui/DataTable'
import { SaleShop, UpdateSaleShopData, Currency, Transaction } from '@/types'
import { saleShopService } from '@/services/saleShopService'
import { helpersService } from '@/services/helpersService'
import { transactionService } from '../services/transactionService'
import APIKeyManagement from '@/components/saleShop/APIKeyManagement'
import { formatDistanceToNow } from 'date-fns'
import { TableColumn, TableAction, TablePagination, TableSort, TableFilter } from '@/types/table'

export default function SaleShopDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [saleShop, setSaleShop] = useState<SaleShop | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [editing, setEditing] = useState(false)
  const [currencies, setCurrencies] = useState<Currency[]>([])
  const [popularCurrencies, setPopularCurrencies] = useState<Currency[]>([])
  const [currencySearchQuery, setCurrencySearchQuery] = useState('')
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false)
  const [currencyLoading, setCurrencyLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [success, setSuccess] = useState<string>('')
  const [formData, setFormData] = useState<UpdateSaleShopData>({})
  const currencyDropdownRef = useRef<HTMLDivElement>(null)

  // Transaction states
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [transactionLoading, setTransactionLoading] = useState(true)
  const [transactionError, setTransactionError] = useState<string | null>(null)
  const [transactionStats, setTransactionStats] = useState({
    total: 0,
    completed: 0,
    pending: 0,
    failed: 0,
    processing: 0,
    cancelled: 0,
    refunded: 0,
  })
  const [transactionPagination, setTransactionPagination] = useState<TablePagination>({
    current: 1,
    pageSize: 10,
    total: 0,
    pageSizeOptions: [5, 10, 20, 50],
  })
  const [transactionSort, setTransactionSort] = useState<TableSort | undefined>({
    key: 'created_at',
    direction: 'desc',
  })
  const [transactionFilters, setTransactionFilters] = useState<TableFilter[]>([])

  useEffect(() => {
    if (id) {
      fetchSaleShop()
      fetchCurrencies()
      fetchPopularCurrencies()
      fetchTransactionStats()
      fetchTransactions({
        page: transactionPagination.current,
        pageSize: transactionPagination.pageSize,
        sort: transactionSort,
        filters: transactionFilters,
        search: '',
      })
    }
  }, [id])

  const fetchTransactionStats = async () => {
    if (!id) return

    try {
      // Fetch all transactions for stats (no pagination)
      const response = await transactionService.getSaleShopTransactions(parseInt(id), {
        page: 1,
        page_size: 1000, // Get all transactions for stats
      })

      if (response.success) {
        const { results } = response.data
        const stats = {
          total: results.length,
          completed: results.filter(t => t.status === 'completed').length,
          pending: results.filter(t => t.status === 'pending').length,
          failed: results.filter(t => t.status === 'failed').length,
          processing: results.filter(t => t.status === 'processing').length,
          cancelled: results.filter(t => t.status === 'cancelled').length,
          refunded: results.filter(t => t.status === 'refunded').length,
        }
        setTransactionStats(stats)
      }
    } catch (error: any) {
      console.error('Failed to fetch transaction stats:', error)
    }
  }

  const fetchTransactions = async (params: {
    page: number
    pageSize: number
    sort?: TableSort
    filters: TableFilter[]
    search?: string
  }): Promise<{ data: Transaction[]; total: number }> => {
    if (!id) return { data: [], total: 0 }

    setTransactionLoading(true)
    setTransactionError(null)
    try {
      // Convert filters to API parameters
      const statusFilter = params.filters.find(f => f.key === 'status')
      const paymentMethodFilter = params.filters.find(f => f.key === 'payment_method')

      const response = await transactionService.getSaleShopTransactions(parseInt(id), {
        page: params.page,
        page_size: params.pageSize,
        search: params.search || undefined,
        status: statusFilter ? statusFilter.value : undefined,
        payment_method: paymentMethodFilter ? paymentMethodFilter.value : undefined,
      })

      if (response.success) {
        const { results, count } = response.data
        setTransactions(results)
        setTransactionPagination(prev => ({
          ...prev,
          current: params.page,
          total: count,
        }))
        return { data: results, total: count }
      } else {
        setTransactionError(response.message || 'Failed to fetch transactions')
        return { data: [], total: 0 }
      }
    } catch (error: any) {
      const errorMessage = transactionService.handleError
        ? transactionService.handleError(error)
        : 'Failed to fetch transactions'
      setTransactionError(errorMessage)
      console.error('Failed to fetch transactions:', error)
      return { data: [], total: 0 }
    } finally {
      setTransactionLoading(false)
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        currencyDropdownRef.current &&
        !currencyDropdownRef.current.contains(event.target as Node)
      ) {
        setShowCurrencyDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const fetchSaleShop = async () => {
    if (!id) return

    setLoading(true)
    try {
      const response = await saleShopService.getSaleShop(parseInt(id))
      if (response.success) {
        setSaleShop(response.data)
        setFormData({
          name: response.data.name,
          description: response.data.description,
          website_url: response.data.website_url,
          webhook_url: response.data.webhook_url,
          redirect_url: response.data.redirect_url,
          cancel_url: response.data.cancel_url,
          currency_id: response.data.currency,
          is_active: response.data.is_active,
        })
      }
    } catch (error: any) {
      console.error('Failed to fetch sale shop:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchCurrencies = async () => {
    try {
      const data = await helpersService.getCurrencies()
      setCurrencies(data)
    } catch (error) {
      console.error('Failed to fetch currencies:', error)
    }
  }

  const fetchPopularCurrencies = async () => {
    try {
      const data = await helpersService.getPopularCurrencies()
      setPopularCurrencies(data)
    } catch (error) {
      console.error('Failed to fetch popular currencies:', error)
    }
  }

  const searchCurrencies = async (query: string) => {
    if (!query.trim()) {
      setCurrencies([])
      return
    }

    setCurrencyLoading(true)
    try {
      const data = await helpersService.searchCurrencies(query)
      setCurrencies(data)
    } catch (error) {
      console.error('Failed to search currencies:', error)
    } finally {
      setCurrencyLoading(false)
    }
  }

  const handleCurrencySearch = (query: string) => {
    setCurrencySearchQuery(query)
    if (query.length >= 2) {
      searchCurrencies(query)
    } else if (query.length === 0) {
      fetchCurrencies()
    }
  }

  const selectCurrency = (currency: Currency) => {
    setFormData(prev => ({
      ...prev,
      currency_id: currency.id,
    }))
    setCurrencySearchQuery(`${currency.code} - ${currency.name}`)
    setShowCurrencyDropdown(false)
  }

  const getSelectedCurrency = () => {
    if (!formData.currency_id) return null
    return (
      currencies.find(c => c.id === formData.currency_id) ||
      popularCurrencies.find(c => c.id === formData.currency_id)
    )
  }

  const handleInputChange = (field: keyof UpdateSaleShopData, value: string | number | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }))

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (formData.name !== undefined && !formData.name.trim()) {
      newErrors.name = 'Sale shop name is required'
    }

    if (formData.website_url && !isValidUrl(formData.website_url)) {
      newErrors.website_url = 'Website URL must be a valid URL starting with http:// or https://'
    }

    if (formData.webhook_url && !isValidUrl(formData.webhook_url)) {
      newErrors.webhook_url = 'Webhook URL must be a valid URL starting with http:// or https://'
    }

    if (formData.redirect_url && !isValidUrl(formData.redirect_url)) {
      newErrors.redirect_url = 'Redirect URL must be a valid URL starting with http:// or https://'
    }

    if (formData.cancel_url && !isValidUrl(formData.cancel_url)) {
      newErrors.cancel_url = 'Cancel URL must be a valid URL starting with http:// or https://'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const isValidUrl = (url: string): boolean => {
    try {
      const urlObj = new URL(url)
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:'
    } catch {
      return false
    }
  }

  const handleSave = async () => {
    if (!id || !validateForm()) {
      return
    }

    setSaving(true)
    try {
      const response = await saleShopService.updateSaleShop(parseInt(id), formData)
      if (response.success) {
        setSaleShop(response.data)
        setEditing(false)
        setSuccess('Sale shop updated successfully!')
        setTimeout(() => setSuccess(''), 3000)
      }
    } catch (error: any) {
      const errorMessage = saleShopService.handleError(error)
      setErrors({ general: errorMessage })
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    if (saleShop) {
      setFormData({
        name: saleShop.name,
        description: saleShop.description,
        website_url: saleShop.website_url,
        webhook_url: saleShop.webhook_url,
        redirect_url: saleShop.redirect_url,
        cancel_url: saleShop.cancel_url,
        currency_id: saleShop.currency,
        is_active: saleShop.is_active,
      })
    }
    setEditing(false)
    setErrors({})
  }

  // Define transaction table columns
  const transactionColumns: TableColumn<Transaction>[] = [
    {
      key: 'transaction_reference',
      title: 'Reference',
      dataIndex: 'transaction_reference',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (value, record) => (
        <div className="flex items-center space-x-3">
          <div
            className={`p-2 rounded-lg ${record.status === 'completed'
              ? 'bg-green-500/20'
              : record.status === 'pending'
                ? 'bg-yellow-500/20'
                : record.status === 'failed'
                  ? 'bg-red-500/20'
                  : 'bg-gray-500/20'
              }`}
          >
            <CreditCard
              className={`h-5 w-5 ${record.status === 'completed'
                ? 'text-green-400'
                : record.status === 'pending'
                  ? 'text-yellow-400'
                  : record.status === 'failed'
                    ? 'text-red-400'
                    : 'text-gray-400'
                }`}
            />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-white truncate">{value}</div>
            {record.external_reference && (
              <div className="text-sm text-gray-300 truncate max-w-xs">
                Ext: {record.external_reference}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      dataIndex: 'status',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Pending', value: 'pending' },
        { label: 'Processing', value: 'processing' },
        { label: 'Completed', value: 'completed' },
        { label: 'Failed', value: 'failed' },
        { label: 'Cancelled', value: 'cancelled' },
        { label: 'Refunded', value: 'refunded' },
      ],
      render: value => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${value === 'completed'
            ? 'text-green-800 bg-green-100'
            : value === 'pending'
              ? 'text-yellow-800 bg-yellow-100'
              : value === 'processing'
                ? 'text-blue-800 bg-blue-100'
                : value === 'failed'
                  ? 'text-red-800 bg-red-100'
                  : value === 'cancelled'
                    ? 'text-gray-800 bg-gray-100'
                    : 'text-orange-800 bg-orange-100'
            }`}
        >
          {value.charAt(0).toUpperCase() + value.slice(1)}
        </span>
      ),
    },
    {
      key: 'total_amount',
      title: 'Amount',
      dataIndex: 'total_amount',
      sortable: true,
      filterable: false,
      render: (value, record) => (
        <div className="text-sm">
          <span className="text-white font-medium">
            {record.currency}
            {Number(value ? value : 0).toFixed(2)}
          </span>
          <span className="text-gray-300 ml-1">{record.currency}</span>
        </div>
      ),
    },
    {
      key: 'payment_method',
      title: 'Payment Method',
      dataIndex: 'payment_method',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Card', value: 'card' },
        { label: 'Bank Transfer', value: 'bank_transfer' },
        { label: 'Mobile Money', value: 'mobile_money' },
        { label: 'Crypto', value: 'crypto' },
        { label: 'Other', value: 'other' },
      ],
      render: value => (
        <span className="text-sm text-white">
          {value
            ? value
              .split('_')
              .map((word: any) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ')
            : 'N/A'}
        </span>
      ),
    },
    {
      key: 'created_at',
      title: 'Created',
      dataIndex: 'created_at',
      sortable: true,
      filterable: false,
      render: value => (
        <span className="text-sm text-gray-300">
          {formatDistanceToNow(new Date(value), { addSuffix: true })}
        </span>
      ),
    },
  ]

  // Define table actions
  const transactionActions: TableAction<Transaction>[] = [
    {
      key: 'view',
      label: 'View',
      icon: <Eye className="w-4 h-4" />,
      onClick: record => {
        navigate(`/transactions/${record.id}`)
      },
    },
  ]

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900">
        <div className="p-6">
          <div className="transition-all duration-300 border card hover:shadow-xl bg-slate-800/80 backdrop-blur-sm border-gray-700/50">
            <div className="p-6 card-content">
              <div className="text-center py-12">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-white/60" />
                <p className="text-white/80">Loading sale shop details...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!saleShop) {
    return (
      <div className="min-h-screen bg-gray-900">
        <div className="p-6">
          <div className="transition-all duration-300 border card hover:shadow-xl bg-slate-800/80 backdrop-blur-sm border-gray-700/50">
            <div className="p-6 card-content">
              <div className="text-center py-12">
                <XCircleIcon className="h-12 w-12 mx-auto mb-4 text-white/60" />
                <p className="text-white/80 mb-4">Sale shop not found</p>
                <Button onClick={() => navigate('/sale-shops')} className="btn-gradient">
                  Back to Sale Shops
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <div className="relative z-10 p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between bg-slate-800/80 backdrop-blur-sm rounded-xl p-4 border border-gray-700/50 gap-4">
            <div className="flex items-center space-x-3">
              <Button
                variant="ghost"
                onClick={() => navigate('/sale-shops')}
                className="p-1.5 hover:bg-white/10 rounded-lg text-white"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <ShoppingCart className="h-5 w-5 text-orange-400" />
              </div>
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2">
                  <h1 className="text-xl font-semibold text-white">{saleShop.name}</h1>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${saleShop.is_active
                      ? 'text-green-400 bg-green-500/20'
                      : 'text-gray-400 bg-gray-500/20'
                      }`}
                  >
                    {saleShop.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p className="text-gray-400 text-sm mt-0.5">
                  {saleShop.description || 'No description provided'}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => setEditing(!editing)}
              className="border-gray-600 text-white hover:bg-white/10 w-full sm:w-auto"
              size="sm"
            >
              <Edit className="h-4 w-4 mr-1.5" />
              {editing ? 'Cancel' : 'Edit'}
            </Button>
          </div>
        </div>

        {/* Messages */}
        {errors.general && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-lg flex items-center space-x-3">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
            <p className="text-red-200">{errors.general}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-500/20 border border-green-500/30 rounded-lg flex items-center space-x-3">
            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
            <p className="text-green-200">{success}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-9 space-y-6">
            {/* Basic Information */}
            <div className="transition-all duration-300 border card hover:shadow-xl bg-slate-800/80 backdrop-blur-sm border-gray-700/50">
              <div className="p-6 card-content">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
                  <h2 className="text-lg font-semibold text-white flex items-center space-x-3">
                    <div className="p-2 bg-blue-500/20 rounded-lg">
                      <Settings className="h-5 w-5 text-blue-400" />
                    </div>
                    <span>Basic Information</span>
                  </h2>
                  {editing && (
                    <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                      <Button
                        onClick={handleCancel}
                        variant="outline"
                        className="border-gray-600 text-white hover:bg-white/10 w-full sm:w-auto"
                        disabled={saving}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleSave}
                        className="btn-gradient w-full sm:w-auto"
                        disabled={saving}
                      >
                        {saving ? (
                          <>
                            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="h-5 w-5 mr-2" />
                            Save Changes
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">
                      Channel Name
                    </label>
                    {editing ? (
                      <input
                        type="text"
                        value={formData.name || ''}
                        onChange={e => handleInputChange('name', e.target.value)}
                        className={`input w-full py-2.5 px-3 text-sm bg-white/5 border rounded-lg transition-all duration-200 hover:border-white/30 text-white ${errors.name
                          ? 'border-red-500 focus:border-red-500 focus:ring-4 focus:ring-red-500/20'
                          : 'border-gray-600 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/20'
                          }`}
                        placeholder="Enter channel name"
                      />
                    ) : (
                      <p className="text-white py-2.5 px-3 bg-white/5 rounded-lg border border-gray-600">
                        {saleShop.name}
                      </p>
                    )}
                    {errors.name && (
                      <p className="text-red-400 text-sm mt-2 flex items-center space-x-2">
                        <XCircleIcon className="h-4 w-4 flex-shrink-0" />
                        <span>{errors.name}</span>
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">Currency</label>
                    {editing ? (
                      <div className="relative" ref={currencyDropdownRef}>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/40" />
                          <input
                            type="text"
                            value={currencySearchQuery}
                            onChange={e => handleCurrencySearch(e.target.value)}
                            onFocus={() => setShowCurrencyDropdown(true)}
                            placeholder="Search currencies (e.g., USD, Euro, Dollar)"
                            className="input w-full pl-9 pr-9 py-2.5 text-sm bg-white/5 border rounded-lg border-gray-600 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/20 transition-all duration-200 hover:border-white/30 text-white placeholder-white/40"
                          />
                          <button
                            type="button"
                            onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/40 hover:text-white/60"
                          >
                            <ChevronDown
                              className={`h-4 w-4 transition-transform ${showCurrencyDropdown ? 'rotate-180' : ''}`}
                            />
                          </button>
                        </div>

                        {showCurrencyDropdown && (
                          <div className="absolute z-50 w-full mt-1 bg-slate-800/95 backdrop-blur-lg border border-gray-600 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                            {currencySearchQuery.length === 0 && popularCurrencies.length > 0 && (
                              <div className="p-3 border-b border-gray-600">
                                <div className="flex items-center space-x-2 text-sm font-medium text-white/70">
                                  <Star className="h-4 w-4 text-yellow-400" />
                                  <span>Popular Currencies</span>
                                </div>
                              </div>
                            )}

                            {currencySearchQuery.length === 0 && popularCurrencies.length > 0 && (
                              <div className="max-h-32 overflow-y-auto">
                                {popularCurrencies.map(currency => (
                                  <button
                                    key={currency.id}
                                    type="button"
                                    onClick={() => selectCurrency(currency)}
                                    className="w-full px-4 py-3 text-left hover:bg-white/5 flex items-center justify-between text-white"
                                  >
                                    <div>
                                      <div className="font-medium text-white">
                                        {currency.code} - {currency.name}
                                      </div>
                                      <div className="text-sm text-white/60">
                                        {currency.symbol} • {currency.decimal_places} decimal places
                                      </div>
                                    </div>
                                    <Star className="h-4 w-4 text-yellow-400" />
                                  </button>
                                ))}
                              </div>
                            )}

                            {currencySearchQuery.length > 0 && (
                              <div className="p-3 border-b border-gray-600">
                                <div className="text-sm font-medium text-white/70">
                                  Search Results
                                </div>
                              </div>
                            )}

                            <div className="max-h-32 overflow-y-auto">
                              {currencyLoading ? (
                                <div className="p-4 text-center">
                                  <Loader2 className="h-5 w-5 animate-spin mx-auto text-gray-400" />
                                  <p className="text-sm text-gray-300 mt-2">
                                    Searching currencies...
                                  </p>
                                </div>
                              ) : currencies.length > 0 ? (
                                currencies.map(currency => (
                                  <button
                                    key={currency.id}
                                    type="button"
                                    onClick={() => selectCurrency(currency)}
                                    className="w-full px-4 py-3 text-left hover:bg-white/5 flex items-center justify-between text-white"
                                  >
                                    <div>
                                      <div className="font-medium text-white">
                                        {currency.code} - {currency.name}
                                      </div>
                                      <div className="text-sm text-gray-300">
                                        {currency.symbol} • {currency.decimal_places} decimal places
                                      </div>
                                    </div>
                                  </button>
                                ))
                              ) : currencySearchQuery.length > 0 ? (
                                <div className="p-4 text-center text-gray-300">
                                  <p className="text-sm">
                                    No currencies found for "{currencySearchQuery}"
                                  </p>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        )}

                        {formData.currency_id && (
                          <div className="mt-2 p-3 bg-green-500/20 border border-green-500/30 rounded-lg">
                            <div className="flex items-center space-x-2">
                              <CheckCircle className="h-5 w-5 text-green-500" />
                              <div>
                                <div className="font-medium text-green-200">
                                  {getSelectedCurrency()?.code} - {getSelectedCurrency()?.name}
                                </div>
                                <div className="text-sm text-green-300">
                                  Symbol: {getSelectedCurrency()?.symbol} • Decimal places:{' '}
                                  {getSelectedCurrency()?.decimal_places}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-white py-2.5 px-3 bg-white/5 rounded-lg border border-gray-600">
                        {saleShop.currency_code
                          ? `${saleShop.currency_code} - ${saleShop.currency_name}`
                          : 'No currency selected'}
                      </p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-white/70 mb-2">
                      Description
                    </label>
                    {editing ? (
                      <textarea
                        value={formData.description || ''}
                        onChange={e => handleInputChange('description', e.target.value)}
                        rows={3}
                        className="input w-full py-2.5 px-3 text-sm bg-white/5 border rounded-lg border-gray-600 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/20 transition-all duration-200 hover:border-white/30 text-white placeholder-white/40"
                        placeholder="Enter description"
                      />
                    ) : (
                      <p className="text-white py-2.5 px-3 bg-white/5 rounded-lg border border-gray-600">
                        {saleShop.description || 'No description provided'}
                      </p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editing ? (formData.is_active ?? false) : saleShop.is_active}
                        onChange={e => handleInputChange('is_active', e.target.checked)}
                        disabled={!editing}
                        className="w-5 h-5 text-orange-600 border-gray-600 rounded focus:ring-orange-500 disabled:opacity-50 bg-white/5"
                      />
                      <span className="text-sm font-medium text-white/70">Active Channel</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* URLs Configuration */}
            <div className="transition-all duration-300 border card hover:shadow-xl bg-slate-800/80 backdrop-blur-sm border-gray-700/50">
              <div className="p-6 card-content">
                <h2 className="text-lg font-semibold text-white flex items-center space-x-3 mb-6">
                  <div className="p-2 bg-green-500/20 rounded-lg">
                    <Globe className="h-5 w-5 text-green-400" />
                  </div>
                  <span>URLs Configuration</span>
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">
                      Website URL
                    </label>
                    {editing ? (
                      <div className="relative">
                        <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/40" />
                        <input
                          type="url"
                          value={formData.website_url || ''}
                          onChange={e => handleInputChange('website_url', e.target.value)}
                          className={`input w-full pl-9 pr-4 py-2.5 text-sm bg-white/5 border rounded-lg transition-all duration-200 hover:border-white/30 text-white placeholder-white/40 ${errors.website_url
                            ? 'border-red-500 focus:border-red-500 focus:ring-4 focus:ring-red-500/20'
                            : 'border-gray-600 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/20'
                            }`}
                          placeholder="https://yourwebsite.com"
                        />
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2 py-2.5 px-3 bg-white/5 rounded-lg border border-gray-600">
                        {saleShop.website_url ? (
                          <>
                            <Globe className="h-4 w-4 text-white/40" />
                            <a
                              href={saleShop.website_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-400 hover:text-blue-300 flex items-center space-x-1"
                            >
                              <span>{saleShop.website_url}</span>
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </>
                        ) : (
                          <span className="text-gray-400">No website URL configured</span>
                        )}
                      </div>
                    )}
                    {errors.website_url && (
                      <p className="text-red-400 text-sm mt-2 flex items-center space-x-1">
                        <XCircleIcon className="h-4 w-4" />
                        <span>{errors.website_url}</span>
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">
                      Webhook URL
                    </label>
                    {editing ? (
                      <div className="relative">
                        <Webhook className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/40" />
                        <input
                          type="url"
                          value={formData.webhook_url || ''}
                          onChange={e => handleInputChange('webhook_url', e.target.value)}
                          className={`input w-full pl-9 pr-4 py-2.5 text-sm bg-white/5 border rounded-lg transition-all duration-200 hover:border-white/30 text-white placeholder-white/40 ${errors.webhook_url
                            ? 'border-red-500 focus:border-red-500 focus:ring-4 focus:ring-red-500/20'
                            : 'border-gray-600 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/20'
                            }`}
                          placeholder="https://yourwebsite.com/webhooks"
                        />
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2 py-2.5 px-3 bg-white/5 rounded-lg border border-gray-600">
                        {saleShop.webhook_url ? (
                          <>
                            <Webhook className="h-4 w-4 text-white/40" />
                            <span className="text-white">{saleShop.webhook_url}</span>
                          </>
                        ) : (
                          <span className="text-gray-400">No webhook URL configured</span>
                        )}
                      </div>
                    )}
                    {errors.webhook_url && (
                      <p className="text-red-400 text-sm mt-2 flex items-center space-x-1">
                        <XCircleIcon className="h-4 w-4" />
                        <span>{errors.webhook_url}</span>
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">
                      Success Redirect URL
                    </label>
                    {editing ? (
                      <div className="relative">
                        <ExternalLink className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/40" />
                        <input
                          type="url"
                          value={formData.redirect_url || ''}
                          onChange={e => handleInputChange('redirect_url', e.target.value)}
                          className={`input w-full pl-9 pr-4 py-2.5 text-sm bg-white/5 border rounded-lg transition-all duration-200 hover:border-white/30 text-white placeholder-white/40 ${errors.redirect_url
                            ? 'border-red-500 focus:border-red-500 focus:ring-4 focus:ring-red-500/20'
                            : 'border-gray-600 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/20'
                            }`}
                          placeholder="https://yourwebsite.com/success"
                        />
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2 py-2.5 px-3 bg-white/5 rounded-lg border border-gray-600">
                        {saleShop.redirect_url ? (
                          <>
                            <ExternalLink className="h-4 w-4 text-white/40" />
                            <span className="text-white">{saleShop.redirect_url}</span>
                          </>
                        ) : (
                          <span className="text-gray-400">No redirect URL configured</span>
                        )}
                      </div>
                    )}
                    {errors.redirect_url && (
                      <p className="text-red-400 text-sm mt-2 flex items-center space-x-1">
                        <XCircleIcon className="h-4 w-4" />
                        <span>{errors.redirect_url}</span>
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">
                      Cancel Redirect URL
                    </label>
                    {editing ? (
                      <div className="relative">
                        <XCircleIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/40" />
                        <input
                          type="url"
                          value={formData.cancel_url || ''}
                          onChange={e => handleInputChange('cancel_url', e.target.value)}
                          className={`input w-full pl-9 pr-4 py-2.5 text-sm bg-white/5 border rounded-lg transition-all duration-200 hover:border-white/30 text-white placeholder-white/40 ${errors.cancel_url
                            ? 'border-red-500 focus:border-red-500 focus:ring-4 focus:ring-red-500/20'
                            : 'border-gray-600 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/20'
                            }`}
                          placeholder="https://yourwebsite.com/cancel"
                        />
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2 py-2.5 px-3 bg-white/5 rounded-lg border border-gray-600">
                        {saleShop.cancel_url ? (
                          <>
                            <XCircleIcon className="h-4 w-4 text-white/40" />
                            <span className="text-white">{saleShop.cancel_url}</span>
                          </>
                        ) : (
                          <span className="text-gray-400">No cancel URL configured</span>
                        )}
                      </div>
                    )}
                    {errors.cancel_url && (
                      <p className="text-red-400 text-sm mt-2 flex items-center space-x-1">
                        <XCircleIcon className="h-4 w-4" />
                        <span>{errors.cancel_url}</span>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-3 space-y-6">
            <APIKeyManagement saleShopId={saleShop.id} saleShopName={saleShop.name} />
          </div>
        </div>

        {/* Transactions */}
        <div className="transition-all duration-300 border card hover:shadow-xl bg-slate-800/80 backdrop-blur-sm border-gray-700/50 mt-6">
          <div className="p-6 card-content">
            <h2 className="text-lg font-semibold text-white flex items-center space-x-3 mb-6">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <CreditCard className="h-5 w-5 text-orange-400" />
              </div>
              <span>Transactions</span>
            </h2>

            {/* Error Display */}
            {transactionError && (
              <div className="mb-8 p-4 bg-red-500/20 border border-red-500/30 rounded-lg flex items-center space-x-3">
                <XCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-red-200 font-medium">Error loading transactions</p>
                  <p className="text-red-300 text-sm">{transactionError}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    fetchTransactionStats()
                    fetchTransactions({
                      page: transactionPagination.current,
                      pageSize: transactionPagination.pageSize,
                      sort: transactionSort,
                      filters: transactionFilters,
                      search: '',
                    })
                  }}
                  className="text-red-200 border-red-500/30 hover:bg-red-500/20"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retry
                </Button>
              </div>
            )}

            {/* Transaction Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              <div className="transition-all duration-300 border card hover:shadow-xl bg-slate-800/80 backdrop-blur-sm border-gray-700/50">
                <div className="p-4 card-content">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-orange-300 uppercase tracking-wide">
                        Total Transactions
                      </p>
                      <p className="text-xl font-bold text-white mt-1">{transactionStats.total}</p>
                      <p className="text-xs text-gray-300 mt-1">All transactions</p>
                    </div>
                    <div className="p-3 bg-orange-500/20 rounded-xl shadow-lg flex-shrink-0">
                      <CreditCard className="h-6 w-6 text-orange-400" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="transition-all duration-300 border card hover:shadow-xl bg-slate-800/80 backdrop-blur-sm border-gray-700/50">
                <div className="p-4 card-content">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-green-300 uppercase tracking-wide">
                        Completed
                      </p>
                      <p className="text-xl font-bold text-white mt-1">
                        {transactionStats.completed}
                      </p>
                      <p className="text-xs text-gray-300 mt-1">Successful transactions</p>
                    </div>
                    <div className="p-3 bg-green-500/20 rounded-xl shadow-lg flex-shrink-0">
                      <CheckCircle className="h-6 w-6 text-green-400" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="transition-all duration-300 border card hover:shadow-xl bg-slate-800/80 backdrop-blur-sm border-gray-700/50">
                <div className="p-4 card-content">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-yellow-300 uppercase tracking-wide">
                        Pending
                      </p>
                      <p className="text-xl font-bold text-white mt-1">
                        {transactionStats.pending}
                      </p>
                      <p className="text-xs text-gray-300 mt-1">In progress</p>
                    </div>
                    <div className="p-3 bg-yellow-500/20 rounded-xl shadow-lg flex-shrink-0">
                      <Loader2 className="h-6 w-6 text-yellow-400 animate-spin" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="transition-all duration-300 border card hover:shadow-xl bg-slate-800/80 backdrop-blur-sm border-gray-700/50">
                <div className="p-4 card-content">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-red-300 uppercase tracking-wide">
                        Failed
                      </p>
                      <p className="text-xl font-bold text-white mt-1">{transactionStats.failed}</p>
                      <p className="text-xs text-gray-300 mt-1">Failed transactions</p>
                    </div>
                    <div className="p-3 bg-red-500/20 rounded-xl shadow-lg flex-shrink-0">
                      <XCircleIcon className="h-6 w-6 text-red-400" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Transaction Table */}
            <DataTable
              columns={transactionColumns}
              data={transactions}
              loading={transactionLoading}
              pagination={transactionPagination}
              filters={transactionFilters}
              sort={transactionSort}
              actions={transactionActions}
              rowKey="id"
              onSort={setTransactionSort}
              onFilter={setTransactionFilters}
              onPageChange={(page, pageSize) => {
                setTransactionPagination(prev => ({ ...prev, current: page, pageSize }))
                fetchTransactions({
                  page,
                  pageSize,
                  sort: transactionSort,
                  filters: transactionFilters,
                  search: '',
                })
              }}
              onSearch={search => {
                fetchTransactions({
                  page: 1,
                  pageSize: transactionPagination.pageSize,
                  sort: transactionSort,
                  filters: transactionFilters,
                  search,
                })
              }}
              onServerSideChange={fetchTransactions}
              serverSide={true}
              searchable={true}
              searchPlaceholder="Search by reference, customer name, or email..."
              emptyText="No transactions found"
              emptyIcon={<CreditCard className="h-12 w-12 text-gray-300" />}
              loadingText="Loading transactions..."
              showStats={true}
              showSearch={true}
              showFilters={true}
              showPagination={true}
              showActions={true}
              striped={true}
              hoverable={true}
              bordered={false}
              className="!bg-transparent !border-none !rounded-none"
            />
          </div>
        </div>
      </div>
    </div>
  )
}
